import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL;
const STATUS_COLORS = { Open:'#60a5fa', 'In Progress':'#f59e0b', Completed:'#46BB89', Cancelled:'#94a3b8' };

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || '#666';
  return <span style={{ display:'inline-block', padding:'2px 10px', borderRadius:20, fontSize:11,
    fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase', background:c+'22', color:c }}>{status}</span>;
}
function normalizeRole(r) {
  if (!r) return ''; if (Array.isArray(r)) return (r[0]||'').toLowerCase().trim();
  return String(r).toLowerCase().trim();
}

export default function WorkOrderList() {
  const { getToken } = useAuth(); const { user } = useUser(); const navigate = useNavigate();
  const role = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const canCreate = ['admin','quality-manager','production-engineer','service'].includes(role);

  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      const res = await window.fetch(`${API_BASE}/quality/work-orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.ok) setOrders(data.orders || []);
    } catch {} finally { setLoading(false); }
  }, [getToken, status]);

  useEffect(() => { load(); }, [load]);

  const fs = { background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)', borderRadius:6, padding:'6px 10px', fontSize:13 };

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:800 }}>Work Orders</h2>
        {canCreate && (
          <button onClick={() => navigate('/quality/execution/new')}
            style={{ background:'#60a5fa', color:'#000', border:'none', borderRadius:6,
              padding:'8px 18px', fontWeight:700, fontSize:13, cursor:'pointer' }}>+ New Work Order</button>
        )}
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:16 }}>
        <select value={status} onChange={e => setStatus(e.target.value)} style={fs}>
          <option value="">All statuses</option>
          {['Open','In Progress','Completed','Cancelled'].map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      <div style={{ background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)', borderRadius:10, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center', color:'var(--ni-text-muted)' }}>Loading…</div>
        : orders.length === 0 ? (
          <div style={{ padding:40, textAlign:'center', color:'var(--ni-text-muted)' }}>
            No work orders found.{' '}
            {canCreate && <button onClick={() => navigate('/quality/execution/new')}
              style={{ background:'none', border:'none', color:'#60a5fa', cursor:'pointer', fontSize:14 }}>Create one.</button>}
          </div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ color:'var(--ni-text-muted)', fontSize:11, textTransform:'uppercase',
                letterSpacing:'0.06em', background:'rgba(0,0,0,0.2)' }}>
                {['WO Number','Process Flow','Devices','Status','Created By','Date'].map(h => (
                  <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontWeight:600, whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o._id} onClick={() => navigate(`/quality/execution/${o._id}`)}
                  style={{ cursor:'pointer', borderTop:'1px solid var(--ni-border-soft)' }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.03)'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                  <td style={{ padding:'11px 16px', fontFamily:'monospace', fontSize:12, color:'#60a5fa', whiteSpace:'nowrap' }}>{o.workOrderNumber}</td>
                  <td style={{ padding:'11px 16px' }}>
                    <div style={{ fontWeight:600 }}>{o.flowName}</div>
                    <div style={{ fontSize:11, color:'var(--ni-text-muted)', fontFamily:'monospace' }}>{o.flowDocNumber}</div>
                  </td>
                  <td style={{ padding:'11px 16px' }}>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      {o.activeCount  > 0 && <span style={{ fontSize:11, color:'#60a5fa',  background:'#60a5fa18',  borderRadius:4, padding:'1px 6px' }}>{o.activeCount} active</span>}
                      {o.passedCount  > 0 && <span style={{ fontSize:11, color:'#46BB89',  background:'#46BB8918',  borderRadius:4, padding:'1px 6px' }}>{o.passedCount} passed</span>}
                      {o.reworkCount  > 0 && <span style={{ fontSize:11, color:'#f59e0b',  background:'#f59e0b18',  borderRadius:4, padding:'1px 6px' }}>{o.reworkCount} rework</span>}
                      {o.scrappedCount> 0 && <span style={{ fontSize:11, color:'#EC2124',  background:'#EC212418',  borderRadius:4, padding:'1px 6px' }}>{o.scrappedCount} scrapped</span>}
                      {o.deviceCount === 0 && <span style={{ fontSize:11, color:'var(--ni-text-muted)' }}>—</span>}
                    </div>
                  </td>
                  <td style={{ padding:'11px 16px', whiteSpace:'nowrap' }}><StatusBadge status={o.status} /></td>
                  <td style={{ padding:'11px 16px', color:'var(--ni-text-muted)' }}>{o.createdByName}</td>
                  <td style={{ padding:'11px 16px', color:'var(--ni-text-muted)', whiteSpace:'nowrap' }}>
                    {o.createdAt ? new Date(o.createdAt).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div style={{ marginTop:10, color:'var(--ni-text-muted)', fontSize:12 }}>{orders.length} work order{orders.length!==1?'s':''}</div>
    </div>
  );
}
